import React, { useState, useEffect } from "react";
import ResearchQueryResultsWithIssues from "../../components/Rules/ResearchQueryResultsWithIssues";
import useFetchGenericResearchIssues from "../../hooks/useFetchGenericResearchIssues";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import { IconIncidentDetails } from "../../common/utils/icons";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";

import {
  getFormattedDateTime,
  calculateDaysInQueue,
} from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { getConfig } from "../../config/runtimeConfig";

interface IGenericResearchQueryIssueResults {
  dashboardData: any;
  setIsResearchQueryLoading: React.Dispatch<React.SetStateAction<boolean>>;
  resourceKeys: any;
  isFilterHide: boolean;
  isResearchQueryLoading: boolean;
}

const GenericResearchQueryIssueResults = ({
  dashboardData,
  setIsResearchQueryLoading,
  resourceKeys,
  isFilterHide,
  isResearchQueryLoading,
}: IGenericResearchQueryIssueResults) => {
  const { hasPermission } = useAuth();
  const canEditGenericResearchQuery = hasPermission(
    "research_query_execution_issues",
    "edit"
  );

  const [selectedQueryName, setSelectedQueryName] = useState<any>("");
  const [columnsData, setColumnsData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [pageN, setPageN] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pageS, setPageS] = useState<number>(rowsPerPage);

  const [dashboardUserDetailsData, setDashboardUserDetailsData] = useState<any>(
    {
      status: false,
      incidentId: null,
      assigned_user: null,
      panelType: "",
      isBackDropVisible: false,
      comment: [],
    }
  );
  const [userDialogData, setUserDialogData] = useState<any>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });

  // Check if the selected query has an error in research_query_results
  const hasQueryError = () => {
    if (!dashboardData?.research_query_results || !selectedQueryName) {
      return false;
    }

    const queryResults =
      dashboardData.research_query_results[selectedQueryName];
    if (
      !queryResults ||
      !Array.isArray(queryResults) ||
      queryResults.length === 0
    ) {
      return false;
    }

    // Check if the first item in the array has an error property
    return queryResults[0]?.hasOwnProperty("error");
  };

  // Get the error message for the selected query
  const getQueryErrorMessage = () => {
    if (!hasQueryError()) {
      return null;
    }

    const queryResults =
      dashboardData.research_query_results[selectedQueryName];
    return queryResults[0]?.error || "An unknown error occurred";
  };

  // Fetch research issues data - only enabled if there's no error
  const [researchIssuesData, refetchResearchIssues] =
    useFetchGenericResearchIssues({
      executionId: dashboardData?.id || null,
      queryName: selectedQueryName,
      page: pageN,
      pageSize: pageS,
      setIsLoading: setIsResearchQueryLoading,
      enabled: !!dashboardData?.id && !!selectedQueryName && !hasQueryError(),
      isIssuesDataAvailable: dashboardData?.is_issues_data_available ?? false,
    });

  useEffect(() => {
    if (dashboardData?.execution_params?.research_queries?.length > 0) {
      setSelectedQueryName(
        dashboardData?.execution_params?.research_queries[0]?.name
      );
    }
  }, [dashboardData]);

  useEffect(() => {
    // Check if there's an error for the selected query
    if (hasQueryError()) {
      const errorMsg = getQueryErrorMessage();
      setErrorMessage(errorMsg);
      setColumnsData([]);
      setRowsData([]);
      return;
    }

    // Clear error message if no error
    setErrorMessage(null);

    if (researchIssuesData?.items?.length > 0) {
      // Create rows from research issues data
      const rows = researchIssuesData?.items.map((item: any) => {
        const researchResult =
          (dashboardData?.is_issues_data_available
            ? item?.generic_research_record_details?.research_query_result
            : item?.research_query_result) ?? {};

        // Calculate days in queue
        const diffInDays = calculateDaysInQueue(item.first_occurrence);

        return {
          // id: index,
          rowId: item.id, // Use the actual issue ID for operations
          current_status: item.current_status,
          assigned_user: item.assigned_user,
          comment: item.comment,
          create_date: item.create_date,
          update_date: item.update_date,
          first_occurrence: item.first_occurrence,
          assigned_by: item.assigned_by,
          issue_type: item.issue_type,
          rule_execution_id: item.rule_execution_id,
          days_in_queue: diffInDays,
          issue_classification_id: item?.issue_classification_id,
          issue_reason_id: item?.issue_reason_id,
          ...researchResult, // Spread the research query result data
        };
      });

      // Create columns from the first research result object
      const firstResult = dashboardData?.is_issues_data_available
        ? researchIssuesData?.items[0]?.generic_research_record_details
            ?.research_query_result
        : researchIssuesData?.items[0]?.research_query_result;

      let dynamicColumns: any[] = [];

      if (firstResult) {
        dynamicColumns = Object.keys(firstResult).map((key) => ({
          field: key,
          headerName: (
            <Tooltip title={`${key}`} placement={"top"} arrow>
              <span>{`${key.slice(0, 15)}${
                key.length >= 15 ? "..." : ""
              } `}</span>
            </Tooltip>
          ),
          width: 160,
        }));
      }

      // Add days in queue column
      const daysInQueueColumn = [
        {
          field: "days_in_queue",
          headerName: "Age",
          flex: 1,
          minWidth: 110,
          renderCell: (params: any) => {
            return (
              <Box>
                {params?.row?.first_occurrence === null ? (
                  params.value ?? 0
                ) : (
                  <Tooltip
                    title={
                      <Typography
                        sx={{ fontSize: "0.875rem" }}
                      >{`First Occurrence : ${getFormattedDateTime(
                        params?.row?.first_occurrence
                      )}`}</Typography>
                    }
                    placement="top"
                    className="tooltip-ellipsis"
                  >
                    {params.value ?? 0}
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      ];

      // Add issue management columns
      const issueColumns = [
        ...(dashboardData?.is_issues_data_available
          ? [
              CurrentStatusColumn({}),
              UserColumn({}),
              CommentColumn({}),
              ClassificationColumn({}),
              ReasonColumn({}),
              {
                field: "action",
                headerName: "Actions",
                align: "center",
                headerAlign: "center",
                minWidth: 130,
                pinned: "right",
                renderCell: (params: any) => {
                  return (
                    canEditGenericResearchQuery && (
                      <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
                        <Tooltip
                          title={`Issue Details : ${params?.row?.rowId}`}
                          placement="top"
                          arrow
                        >
                          <Button
                            className="btn-blue btn-orange btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDashboardUserDetailsData((prev: any) => ({
                                ...prev,
                                status: true,
                                incidentId: params.row.rowId,
                                assigned_user: params.row.assigned_user,
                                panelType: "incidentDetail",
                                isBackDropVisible: true,
                                first_occurrence: params.row.first_occurrence,
                                issue_classification_id:
                                  params.row.issue_classification_id,
                                issue_reason_id: params.row.issue_reason_id,
                                current_status: params.row.current_status,
                                comment: [
                                  {
                                    comment: params.row.comment,
                                    create_date: params.row.create_date,
                                  },
                                ],
                              }));
                            }}
                          >
                            <IconIncidentDetails />
                          </Button>
                        </Tooltip>
                      </Box>
                    )
                  );
                },
              },
              ...daysInQueueColumn,
            ]
          : []),
      ];

      // Combine all columns
      const allColumns = [...dynamicColumns, ...issueColumns];

      setColumnsData(allColumns);
      setRowsData(rows);
    } else {
      setColumnsData([]);
      setRowsData([]);
    }
  }, [
    researchIssuesData,
    resourceKeys,
    setDashboardUserDetailsData,
    setUserDialogData,
    selectedQueryName,
    dashboardData?.research_query_results,
    dashboardData?.is_issues_data_available,
  ]);

  return (
    <ResearchQueryResultsWithIssues
      isLoading={isResearchQueryLoading}
      setIsLoading={setIsResearchQueryLoading}
      isFilterHide={isFilterHide}
      selectedQueryName={selectedQueryName}
      setSelectedQueryName={setSelectedQueryName}
      queryTabs={dashboardData?.execution_params?.research_queries}
      pageN={pageN}
      setPageN={setPageN}
      pageS={pageS}
      setPageS={setPageS}
      totalPages={researchIssuesData?.total}
      refetchResearchIssues={refetchResearchIssues}
      columnsData={columnsData}
      rowsData={rowsData}
      setDashboardUserDetailsData={setDashboardUserDetailsData}
      dashboardUserDetailsData={dashboardUserDetailsData}
      setUserDialogData={setUserDialogData}
      userDialogData={userDialogData}
      isIssuesDataAvailable={dashboardData?.is_issues_data_available}
      errorMessage={errorMessage}
    />
  );
};

export default GenericResearchQueryIssueResults;
